<template>
    <ModalComponent
        :is-open="isModalOpen"
        :show-footer-buttons="true"
        :title="singlePipeline ? `Update Tags on Pipeline` : `Bulk Update Tags on ${selectedPipelineCount} Pipeline(s)`"
        size="lg"
        position="center"
        @update:isOpen="handleClose">
        <div class="p-4">
            <!-- Tags Multiselect -->
            <div class="mb-10">
                <label class="block text-sm font-medium text-gray-700 mb-1">Select Tags</label>
                <multiselect
                    v-model="selectedTags"
                    :options="tagOptions"
                    :multiple="true"
                    :allow-empty="true"
                    :select-label="'Select'"
                    :deselect-label="'Remove'"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    @select="handleTagSelection"
                    placeholder="Select tags to apply"
                    label="name"
                    :class="validationClass"
                    value="id"
                    track-by="id">
                </multiselect>
            </div>

            <div v-if="!selectedTags?.find((t) => t.type?.includes(TAG_TYPES.ENVIRONMENT))" class="rounded-md bg-blue-50 p-4">
                <div class="flex">
                    <div class="text-sm text-blue-700 flex gap-2">
                        <div class="shrink-0">
                            <InformationCircleIcon class="size-5 text-blue-400" aria-hidden="true" />
                        </div>
                        <p>You must include an environment tag, either Production or Development.</p>
                    </div>
                </div>
            </div>

            <div v-if="selectedTags.length" class="rounded-md bg-yellow-50 p-4 mt-4">
                <div class="flex">
                    <div class="text-sm text-yellow-700 flex gap-2">
                        <div class="shrink-0">
                            <ExclamationTriangleIcon class="size-5 text-yellow-400" aria-hidden="true" />
                        </div>
                        <p v-if="!singlePipeline">
                            Saving will completely overwrite all existing tags on the selected pipelines with the selected tags.
                        </p>
                        <p v-else>Saving will completely overwrite all existing tags on this pipeline with the selected tags.</p>
                    </div>
                </div>
            </div>

            <!-- Update All Pipelines Checkbox -->
            <div v-if="!singlePipeline && allSelected && selectedPipelineCount < tagsStore.total" class="my-4 flex items-center">
                <input type="checkbox" v-model="updateAllPipelines" class="mr-2" id="updateAllPipelines" />
                <label for="updateAllPipelines" class="text-sm text-gray-700">I want to update all pipelines in the database</label>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end space-x-2">
                <button class="bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md py-2 px-4 mb-2 w-full min-w-[88px]" @click="handleCancel">
                    Cancel
                </button>

                <LoadingBtn :is-loading="saveIsLoading" @click="handleSave" :can-save="true">Save</LoadingBtn>
            </div>
        </template>
    </ModalComponent>
</template>

<script setup>
import { ref, computed } from 'vue';
import ModalComponent from '@/components/global/modals/Modal.vue';
import Multiselect from 'vue-multiselect';
import LoadingBtn from '@/components/global/buttons/loading-btn.vue';
import { useLoaderStore } from '@/stores/loader.store';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/vue/20/solid';
import { useTagsStore } from '@/stores/tags.store';
import { MAX_ONE_TAG_PER_TYPE, TAG_TYPES } from '@/utils/constants';
import { toast } from 'vue3-toastify';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    selectedPipelineCount: {
        type: Array,
        default: () => []
    },
    allSelected: {
        type: Boolean,
        default: false
    },
    singlePipeline: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:isOpen', 'save']);
const isModalOpen = ref(props.isOpen);
const selectedTags = ref([]);
const updateAllPipelines = ref(false);
const loaderStore = useLoaderStore();
const tagsStore = useTagsStore();

const saveIsLoading = computed(() => loaderStore.isLoading('pipelines.bulkUpdateTags'));
const tagOptions = computed(() => tagsStore.tagsPerTypeForEntityCRUD[TAG_TYPES.PIPELINE]);
const validationClass = computed(() => {
    if (!selectedTags.value.length) {
        return 'border! border-red-500!';
    }

    return 'border! border-gray-300!';
});

const handleClose = (value) => {
    isModalOpen.value = value;
    if (!value) {
        emit('update:isOpen', false);
    }
};

const handleCancel = () => {
    emit('update:isOpen', false);
};

const handleSave = async () => {
    try {
        if (!selectedTags.value?.length) {
            toast.error('Please select at least one tag. Having at least an environment-related tag (Production/Development) is mandatory.');

            return;
        }

        if (!selectedTags.value?.find((tag) => tag.type?.includes(TAG_TYPES.ENVIRONMENT))) {
            toast.error('Please select at least one environment-related tag (Production/Development).');

            return;
        }

        emit('save', { updateAll: updateAllPipelines.value, tags: selectedTags.value?.map((t) => t.id) });
    } catch (error) {
        console.error('Error updating pipelines:', error);
    }
};

const handleTagSelection = () => {
    const existingExclusiveTag = selectedTags.value.filter((tag) => tag.type?.length && MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));

    if (existingExclusiveTag?.length > 1) {
        toast.warning(`Only one tag of the same ${MAX_ONE_TAG_PER_TYPE.join(', ')} type can be selected`);

        selectedTags.value = selectedTags.value.filter((tag) => tag.type?.length && !MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));

        selectedTags.value.push(existingExclusiveTag[0]);
        return;
    }
};
</script>

<style scoped>
/* Add any necessary styling here */
</style>
